import errorCodes from "@/apis/error";
import { orderAdd, orderPreOut, realList, list as stockListApi } from "@/apis/inventoryv2";
import { itemGet } from "@/apis/order";
import ExportProgressModal from "@/components/ExportProgressModal";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import { numFormmater } from "@/pages/Requirement/util";
import { SConModal } from "@/styles/SConModal";
import STableLayout from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { SourceTypeEnum } from "@/types/common";
import { ChangeEnum, ConfirmedEnum, OperateEnum } from "@/types/inventory";
import { InventoryV2, OrderAddV2, OrderItemV2 } from "@/types/inventoryV2";
import { SaleItemState, SaleOrderItemState } from "@/types/sales";
import { Button, Flex, Form, Input, InputNumber, Modal, notification, Spin, TableProps, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SaleOutProps {
  open: boolean;
  list: Array<SaleOrderItemState>;
  onCancel: () => void;
  onSucc?: () => void;
};

type BaseRow = SaleItemState & {
  maxOutQty: number;
  saledQty: number;
  outQty: number;
};

type StockMatchRecord = Record<string, InventoryV2 & { usedCount: number; allocated: number }>;

const SaleOut: FC<SaleOutProps> = ({ open, list, onCancel, onSucc }) => {
  const { t } = useTranslation();
  const [dataList, setDataList] = useState<Array<BaseRow>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [submit, setSubmit] = useState<boolean>(false);
  const [desc, setDesc] = useState<string>('');

  const [needReMatch, setNeedReMatch] = useState<boolean>(true);
  const [matching, setMatching] = useState<boolean>(false);
  const [locTypes, setLocTypes] = useState<number[]>([1]);
  const [stockObj, setStockObj] = useState<StockMatchRecord>({});
  const [realStockObj, setRealStockObj] = useState<StockMatchRecord>({});

  useEffect(() => {
    if (open) dataFetch();
  }, [open])

  const dataFetch = async () => {
    let resData: BaseRow[] = [];
    setLoading(true);
    setProgress(0);

    for (let i = 0; i < list.length; i++) {
      const order = list[i];
      for (let j = 0; j < order.items.length; j++) {
        const item = order.items[j];
        let itemData = await itemGet(item.id);
        //销售总数
        const allQty = (item.quantity || 0) + (item.bquantity || 0);
        //已销售
        let curQuantity = Number(itemData.preOut ?? 0) + Number(itemData.outCount ?? 0);
        //本次出售最大值
        let maxQty = Math.max(allQty - curQuantity, 0);
        resData.push({
          ...itemData,
          order,
          product: { ...item.product! },
          saledQty: curQuantity,
          maxOutQty: maxQty,
          outQty: 0
        })
      }
      setProgress(Math.round((i + 1) / list.length * 100))
    }
    // await matchStockData(resData);
    setLoading(false)
    setDataList([...resData])
  };

  const dataValid = async () => {
    let realItem = dataList.filter(i => !!i.outQty);
    if (!!!realItem.length) {
      notification.error({ message: t('order.zeroTip') });
      return false;
    };
    for (let i = 0; i < realItem.length; i++) {
      const item = realItem[i];
      let itemData = await itemGet(item.id);
      let itemMaxQty = Number(itemData.quantity ?? 0) + Number(itemData.bquantity ?? 0) - Number(itemData.outCount ?? 0) - Number(itemData.preOut ?? 0);
      if (item.outQty > itemMaxQty) {
        setDataList(last => {
          let res = [...last];
          return res.map(ritem => ({
            ...ritem,
            outCount: ritem.id === itemData.id ? itemData.outCount : ritem.outCount,
            preOut: ritem.id === itemData.id ? itemData.preOut : ritem.preOut,
            inCount: ritem.id === itemData.id ? itemData.inCount : ritem.inCount,
            preIn: ritem.id === itemData.id ? itemData.preIn : ritem.preIn,
            maxOutQty: ritem.id === itemData.id ? itemMaxQty : ritem.maxOutQty,
          }));
        })
        notification.error({ message: t('warehouse.outOvernum', { num: item.outQty - itemMaxQty }), description: t('sale.editOutPl') + ": " + (item?.product?.code ?? '') })
        return false;
      }
    };
    return [...realItem];
  }

  const handleQty = (index: number, val: number) => {
    setDataList(last => {
      let res = [...last];
      res[index].outQty = val;

      // 重新计算该商品的 allocated
      const productCode = res[index].product?.code ?? '';
      let newAllocated = 0;
      res.forEach(item => {
        if (item.product?.code === productCode) {
          newAllocated += item.outQty || 0;
        }
      });

      // 更新 stockObj 和 realStockObj 的 allocated
      setStockObj(prev => ({
        ...prev,
        [productCode]: { ...prev[productCode], allocated: newAllocated }
      }));
      setRealStockObj(prev => ({
        ...prev,
        [productCode]: { ...prev[productCode], allocated: newAllocated }
      }));

      return [...res];
    })
  };

  const matchStockData = async (rows?: BaseRow[]) => {
    let matchData = [...(rows || dataList)];
    if (!!matchData.length) {
      setMatching(true);
      try {
        let proCodes = matchData.map(d => d.product?.code ?? '');
        let stockData = await stockListApi({ body: { ids: proCodes } });
        let realStockData = await realList({ body: { ids: proCodes, locationTypes: locTypes } });

        // 构建 stockObj
        let newStockObj: StockMatchRecord = {};
        (stockData.body || []).forEach((stock: InventoryV2) => {
          newStockObj[stock.product.code] = {
            ...stock,
            usedCount: Math.max(stock.count - stock.preOut, 0),
            allocated: 0
          };
        });

        // 构建 realStockObj
        let newRealStockObj: StockMatchRecord = {};
        (realStockData.body || []).forEach((stock: InventoryV2) => {
          newRealStockObj[stock.product.code] = {
            ...stock,
            usedCount: Math.max(stock.count - stock.preOut, 0),
            allocated: 0
          };
        });

        setStockObj(newStockObj);
        setRealStockObj(newRealStockObj);
        setNeedReMatch(false);
      } catch (error) {
        notification.error({ message: t('requirement.matchErr') });
      } finally {
        setMatching(false);
      }
    }
  }

  const handleSubmit = async () => {
    if (submit) return;
    setSubmit(true);

    // 验证库存是否超分配
    for (const [productCode, stockData] of Object.entries(stockObj)) {
      if (stockData.allocated > (stockData.usedCount ?? 0)) {
        notification.error({
          message: t('order.outOvernum'),
          description: productCode
        });
        return setSubmit(false);
      }
    }
    for (const [productCode, realStockData] of Object.entries(realStockObj)) {
      if (realStockData.allocated > (realStockData.usedCount ?? 0)) {
        notification.error({
          message: t('order.outOvernum'),
          description: t('sale.editOutPl') + ": " + productCode
        });
        return setSubmit(false);
      }
    }

    let valid = await dataValid();
    if (valid === false) return setSubmit(false);
    try {
      // let addMap: Record<string, BaseRow[]> = {};
      // valid.forEach(data => {
      //   if (addMap[data.orderId]) addMap[data.orderId].push({ ...data })
      //   else addMap[data.orderId] = [{ ...data }]
      // });
      // let orderNumbers: string[] = [];
      // for (let key in addMap) {
      //   let orderInfo: OrderAddV2 = {
      //     type: ChangeEnum.PRE_OUT,
      //     opType: OperateEnum.TRANSACTION,
      //     desc,
      //     preTime: null,
      //     arrivalAt: null,
      //     sourceId: key,
      //     sourceType: SourceTypeEnum.SALE,
      //     items: addMap[key].map(ri => ({
      //       productId: ri.productId,
      //       lot: '',
      //       thdlot: '',
      //       count: ri.outQty,
      //       desc: '',
      //       locations: [],
      //       sourceId: ri.id,
      //       sourceType: SourceTypeEnum.SALE_ITEM
      //     }))
      //   }
      //   let orderRes = await orderAdd(orderInfo);
      //   orderNumbers.push(orderRes.number);
      //   try { await orderPreOut(orderRes.id, locTypes) } catch (error) { }
      // }
      // notification.success({ message: t('order.orderSucc'), description: orderNumbers.join(',') });
      // setSubmit(false);
      let orderInfo: OrderAddV2 = {
        type: ChangeEnum.PRE_OUT,
        opType: OperateEnum.TRANSACTION,
        desc,
        preTime: null,
        arrivalAt: null,
        items: valid.map(ri => ({
          productId: ri.productId,
          lot: '',
          thdlot: '',
          count: ri.outQty,
          desc: '',
          locations: [],
          sourceId: ri.id,
          sourceType: SourceTypeEnum.SALE_ITEM
        }))
      }
      let orderRes = await orderAdd(orderInfo);
      try { await orderPreOut(orderRes.id, locTypes) } catch (error) { }
      notification.success({ message: t('order.orderSucc'), description: orderRes.number });
      onCancel();
    } catch (error: any) {
      notification.error({ message: errorCodes[error] || t('order.orderErr') });
      setSubmit(false);
    }
  };

  const columns: TableProps<BaseRow>["columns"] = [

    {
      title: <SText text={t("sale.orderNumber")} />,
      dataIndex: ["order", "number"],
      width: 200,
      fixed: 'left',
      render: (_, item) => (
        <Typography.Text
          ellipsis={{ tooltip: item?.order?.number ?? "" }}
          style={{ color: '#1890ff', width: '100%' }}
        >
          {item?.order?.number ?? ""}
        </Typography.Text>
      ),
    },
    // {
    //   title: <SText text={t("sale.innerNumber")} />,
    //   dataIndex: ["order", "innerNumber"],
    //   width: 120,
    //   fixed: 'left',
    //   render: (_, item) => (
    //     <SText text={item?.order?.innerNumber ?? ""} style={{ width: "100%" }} />
    //   ),
    // },
    // {
    //   title: <SText text={t("sale.shortName")} />,
    //   dataIndex: "shortName",
    //   width: 80,
    //   render: (_, item) => (
    //     <SText text={item?.order?.shortName ?? ""} style={{ width: "100%" }} />
    //   ),
    // },
    {
      title: <SText text={t("product.code")} />,
      dataIndex: ["product", "code"],
      width: 120,
      fixed: 'left',
      render: (_, item) => <SText text={item.productId ? (item?.product?.code ?? '') : ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("product.name")} />,
      dataIndex: "name",
      width: 100,
      render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("order.brand")} />,
      dataIndex: "model1",
      width: 120,
      render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      dataIndex: "uom",
      width: 120,
      render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.receivingUnit")} />,
      dataIndex: "receiver",
      width: 140,
      render: (_, item) => (
        <SText text={item?.order?.receiver ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.receivingAddress")} />,
      dataIndex: "receiverAddress",
      width: 140,
      render: (_, item) => (
        <SText text={item?.order?.receiverAddress ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("requirement.saleNum")} />,
      dataIndex: "quantity",
      width: 120,
      render: (_, item) => <SText text={numFormmater((Number(item?.quantity) + Number(item?.bquantity)) || 0)} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("requirement.outCount")} />,
      dataIndex: "saledQty",
      width: 120,
      render: (_, item) => <SText text={numFormmater(item.outCount || 0)} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("warehouse.preOut")} />,
      dataIndex: "saledQty",
      width: 120,
      render: (_, item) => <SText text={numFormmater(item.preOut || 0)} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("requirement.inCount")} />,
      dataIndex: "inCount",
      width: 120,
      render: (_, item) => <SText text={numFormmater(Number(item.inCount ?? 0))} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("requirement.preInCount")} />,
      dataIndex: "inCount",
      width: 120,
      render: (_, item) => <SText text={numFormmater(Number(item.preIn ?? 0))} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("bom.useStock")} />,
      dataIndex: "useStock",
      width: 120,
      render: (_, item) => {
        const productCode = item.product?.code ?? '';
        const stockUseCount = stockObj[productCode]?.usedCount ?? 0;
        return <SText text={numFormmater(stockUseCount)} style={{ width: "100%" }} />;
      },
    },
    {
      title: <SText text={t("bom.realUseStock")} />,
      dataIndex: "realUseStock",
      width: 120,
      render: (_, item) => {
        const productCode = item.product?.code ?? '';
        const allocated = realStockObj[productCode]?.allocated ?? 0;

        const realStockUseCount = realStockObj[productCode]?.usedCount ?? 0;
        return <SText text={numFormmater(realStockUseCount)} style={{ width: "100%", color: allocated > realStockUseCount ? "red" : "#000" }} />;
      },
    },
    {
      title: <SText text={t("bom.allocated")} />,
      dataIndex: "allocated",
      width: 120,
      render: (_, item) => {
        const productCode = item.product?.code ?? '';
        const allocated = stockObj[productCode]?.allocated ?? 0;
        return <SText text={numFormmater(allocated)} style={{ width: "100%", color: allocated > (stockObj[productCode]?.usedCount ?? 0) ? 'red' : 'unset' }} />;
      },
    },
    {
      title: <SText text={t("order.toSaledQty")} />,
      dataIndex: "maxOutQty",
      width: 120,
      fixed: 'right',
      render: (_, item) => <SText text={numFormmater(item.maxOutQty)} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("order.curSaledQty")} />,
      dataIndex: "outQty",
      width: 120,
      fixed: 'right',
      render: (_, item, index) => {
        const productCode = item.product?.code ?? '';
        const stockUsedCount = stockObj[productCode]?.usedCount ?? 0;
        const realStockUsedCount = realStockObj[productCode]?.usedCount ?? 0;

        // 最大值 = maxOutQty 和剩余可用库存的最小值
        const maxValue = Math.max(0, Math.min(item.maxOutQty, stockUsedCount, realStockUsedCount));

        return <InputNumber
          formatter={val => numFormmater(Number(val || 0))}
          min={0}
          max={maxValue}
          precision={0}
          value={item.outQty}
          onChange={(val) => handleQty(index, val || 0)}
          changeOnWheel
        />;
      }
    },
  ];

  return (
    <Modal maskClosable={false}
      title={t('order.saleOut')}
      open={open}
      onCancel={onCancel}
      width={1200}
      footer={
        <Flex style={{ width: "100%" }} align="center" justify="flex-end" gap={6} >
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <SConModal
            title={t('common.tip')}
            content={t('order.orderContitle')}
            onOk={handleSubmit} disable={matching || needReMatch}>
            <Button type="primary"
              loading={submit}
              disabled={matching || needReMatch} >
              {t('common.confirm')}
            </Button>
          </SConModal>
        </Flex>
      }
    >
      <Spin spinning={matching}>
        <Flex vertical style={{ width: "100%", height: "70vh" }}>
          <Flex gap={4} align='baseline' >
            <Form.Item required label={t("requirement.matchLocType")} style={{ width: "100%", marginBottom: 0, }}>
              <SelectLocationType
                mode="multiple"
                style={{ flex: 1 }}
                value={locTypes}
                onChange={val => {
                  setLocTypes(val);
                  !needReMatch && notification.info({ message: t('requirement.rematchTip') })
                  setNeedReMatch(true);
                }}
                maxTagCount={1}
              />
            </Form.Item>
            <Button disabled={!needReMatch || loading} onClick={() => matchStockData()} >{t('requirement.matchInventory')}</Button>
          </Flex>
          <Form.Item layout="vertical" label={t('order.remark')}>
            <Input.TextArea value={desc} onChange={e => setDesc(e.target.value)} autoSize={{ minRows: 2, maxRows: 2 }} maxLength={500} showCount />
          </Form.Item>
          <STableLayout
            headerChildren={null}
            columns={columns as Array<AnyObject>}
            dataSource={dataList}
            total={dataList.length}
            pagination={false}
            extHeight={70}
            scrollX={1000}
          />
        </Flex>
      </Spin>
      <ExportProgressModal visible={loading} progress={progress} title={t('order.calcOut') + '...'} processLabel={t('order.calcOut') + '...'} />
    </Modal>
  )
};
export default SaleOut;